import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, ChevronRight, Sparkles } from "lucide-react";
import { Reveal, Overline } from "./motion";
import { useTranslate } from "../context/TranslateContext";
import { MOVIES, REGIONS } from "../mock/data";

export const Discover = () => {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const listenText = "Discover what to watch. From the Malayalam new wave to Tamil spectacle and Hindi noir, thousands of films across every Indian language, filtered by mood, era, language and where to stream.";

  return (
    <section id="movies" className="relative border-t border-edge bg-surface/30 py-28 md:py-40">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <div className="mb-14 flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-2xl">
            <Overline>Movies · Discovery</Overline>
            <h2 className="mt-4 font-display text-4xl font-semibold leading-tight tracking-tight text-cream md:text-6xl">
              {t("Find your next ")}<span className="italic text-gradient-gold">{t("obsession")}</span>
            </h2>
            <p className="mt-4 font-body text-lg text-ash">
              {t("Filter by language, era, mood, cast and streaming service — then let the AI concierge do the rest.")}
            </p>
            <Link to="/article/movies-overview" data-testid="read-movies-guide"
              className="mt-5 inline-flex items-center gap-2 rounded-full border border-gold/50 px-5 py-2.5 font-body text-sm text-gold transition-colors hover:bg-gold/10">
              {t("Read: Discover Indian Stories")} <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
          
        </div>

        {/* Asymmetric featured grid */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-12">
          {MOVIES.map((m, i) => (
            <Reveal key={m.title} delay={i * 0.06}
              className={i === 0 ? "md:col-span-6 md:row-span-2" : i === 1 ? "md:col-span-6" : "md:col-span-6 lg:col-span-6"}>
              <motion.div whileHover={{ y: -8 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`movie-card-${i}`} onClick={() => navigate(`/movie/${m.id}`)}
                className="group relative cursor-pointer overflow-hidden rounded-2xl border border-edge bg-surface">
                <div className={`relative overflow-hidden ${i === 0 ? "aspect-[4/5] md:aspect-[4/5]" : "aspect-[16/10]"}`}>
                  <img src={m.image} alt={m.title}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/20 to-transparent" />
                  <div className="absolute right-4 top-4 flex items-center gap-1 rounded-full bg-paper/85 px-3 py-1 backdrop-blur">
                    <Star className="h-3.5 w-3.5 fill-gold text-gold" />
                    <span className="font-body text-sm text-cream">{m.rating}</span>
                  </div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="mb-2 flex items-center gap-3 text-xs uppercase tracking-widest text-gold">
                    <span>{m.language}</span><span className="text-paper/60">·</span><span className="text-paper/70">{m.year}</span>
                  </div>
                  <h3 className={`font-display font-semibold text-paper drop-shadow-lg ${i === 0 ? "text-4xl md:text-5xl" : "text-2xl md:text-3xl"}`}>
                    {m.title}
                  </h3>
                  <p className="mt-1 font-body text-sm text-paper/80">{t(m.genre)}</p>
                </div>
              </motion.div>
            </Reveal>
          ))}
        </div>

        {/* Regional strip */}
        <div className="mt-10 flex justify-center">
          <Link to="/movies" data-testid="browse-all-films"
            className="inline-flex items-center gap-2 rounded-full border border-gold/50 px-6 py-3 font-body text-gold transition-colors duration-300 hover:bg-gold/10">
            {t("Browse the full catalogue")} <ChevronRight className="h-4 w-4" />
          </Link>
        </div>

        <div id="cinema" className="mt-24">
          <div className="mb-8 flex items-center gap-4">
            <Overline>Regional Cinema</Overline>
            <div className="h-px flex-1 bg-edge" />
          </div>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {REGIONS.map((r, i) => (
              <Reveal key={r.lang} delay={i * 0.04}>
                <Link to={`/article/industry-${r.lang.toLowerCase()}`} data-testid={`region-card-${i}`}>
                <motion.div whileHover={{ y: -6 }} transition={{ duration: 0.3 }}
                  className="group flex h-full cursor-pointer flex-col justify-between rounded-2xl border border-edge bg-surface p-6 transition-colors duration-300 hover:border-gold/50">
                  <div>
                    <div className="font-display text-3xl text-cream">{r.native}</div>
                    <div className="mt-1 font-body text-sm text-gold">{t(r.lang)}</div>
                  </div>
                  <div className="mt-8">
                    <div className="font-display text-2xl font-semibold text-cream">{r.films}</div>
                    <div className="flex items-center justify-between">
                      <span className="font-body text-xs text-ash">{t(r.note)}</span>
                      <ChevronRight className="h-4 w-4 text-gold opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                    </div>
                  </div>
                </motion.div>
                </Link>
              </Reveal>
            ))}
          </div>

          <div className="mt-10 flex justify-center">
            <Link to="/explore" data-testid="home-deepdives-cta"
              className="inline-flex items-center gap-2 rounded-full border border-gold/50 px-6 py-3 font-body text-gold transition-colors duration-300 hover:bg-gold/10">
              <Sparkles className="h-4 w-4" /> {t("Explore AI Deep Dives")} <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};
